import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { CityworksService } from './cityworks.service';
import { CityworksAuthResponse } from './cityworks-auth-response';
import { CityworksSrResponse } from './cityworks-sr-response';
import { CityworksSrRequest } from './cityworks-sr-request';

const PHONE_REGEX = /^\(?(\d{3})\)?[ .-]?(\d{3})[ .-]?(\d{4})$/;
const PROBLEM_SID = '264003';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  standalone: false
})
export class AppComponent implements OnInit {
  title = 'pucontractors';
  token: string;
  error: string;

  // Submission state
  inProgress = false;
  submitted = false;
  reqid: number;

  // Status check state
  inStatusChkProgress = false;
  srStatus: string;
  statusMessage: string;

  towns = ['Garner', 'Knightdale', 'Rolesville', 'Wake Forest', 'Wendell', 'Zebulon'];

  inspectionTypes: { name: string; note?: string }[] = [
    { name: 'Pre-construction meeting (CORPUD approved/signed plans required)' },
    { name: 'Water main general inspection (Inspection services other than testing)' },
    { name: 'Water main testing' },
    { name: 'Water main flushing - Include main line size in comments', note: 'Note: Flushing of critical mains can have a significant impact on the distribution system. Clearance is required before flushing activities are performed' },
    { name: 'Sewer main general inspections (Inspection services other than testing)' },
    { name: 'Sewer main testing' },
    { name: 'Final site walk-through (Water and Sewer utility acceptance)' },
    { name: 'Other' }
  ];

  addressForm: FormGroup;
  chkReqId: FormGroup;

  minDate: Date;

  constructor(
    private fb: FormBuilder,
    private cityworksService: CityworksService
  ) {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    this.minDate = tomorrow;
  }

  ngOnInit(): void {
    this.cityworksService.getToken().subscribe(
      (data: CityworksAuthResponse) => {
        this.token = data.Value.Token;
      },
      err => (this.error = err.message)
    );

    const checkboxGroup: { [key: string]: FormControl } = {};
    this.inspectionTypes.forEach(type => {
      checkboxGroup[type.name] = new FormControl(false);
    });

    this.addressForm = this.fb.group({
      town: [null, Validators.required],
      project: [null, Validators.required],
      location: [null, Validators.required],
      firstName: [null, Validators.required],
      lastName: [null, Validators.required],
      email: [null],
      company: [null, Validators.required],
      phone: [null, [Validators.required, Validators.pattern(PHONE_REGEX)]],
      inspdate: [null, Validators.required],
      checkboxes: this.fb.group(checkboxGroup),
      notes: [null]
    });

    this.chkReqId = this.fb.group({
      reqid: [null, Validators.required]
    });
  }

  onSubmit(): void {
    if (!this.addressForm.valid) {
      this.addressForm.markAllAsTouched();
      return;
    }

    this.inProgress = true;
    const request = this.buildRequest();

    this.cityworksService.createServiceRequest(request, this.token).subscribe(
      (data: CityworksSrResponse) => {
        this.inProgress = false;
        this.submitted = true;
        this.reqid = data.Value.RequestId;
        this.addressForm.reset();
      },
      err => {
        this.inProgress = false;
        this.error = err.message;
      }
    );
  }

  onSubmitStatusChk(): void {
    if (!this.chkReqId.valid) {
      this.chkReqId.markAllAsTouched();
      return;
    }

    this.inStatusChkProgress = true;
    const requestId = { RequestId: this.chkReqId.get('reqid').value };

    this.cityworksService.getServiceRequest(requestId, this.token).subscribe(
      (data: CityworksSrResponse) => {
        this.inStatusChkProgress = false;
        this.srStatus = data.Value.Status;
        this.statusMessage = `Your service request is in ${this.srStatus} status`;
      },
      err => {
        this.inStatusChkProgress = false;
        this.error = err.message;
      }
    );
  }

  private buildRequest(): CityworksSrRequest {
    const v = this.addressForm.value;
    const request = new CityworksSrRequest();

    request.callerFirstName = v.firstName;
    request.callerLastName = v.lastName;
    request.callerWorkPhone = v.phone;
    request.callerEmail = v.email;
    request.callerAddress = v.location;
    request.address = v.location;
    request.city = v.town;
    request.callerCity = v.town;
    request.problemSid = PROBLEM_SID;

    let newline = 0;
    let comments = `${v.firstName} ${v.lastName},\n`;
    comments += `Date Inspection Needed: ${this.formatDate(v.inspdate)},\n`;
    comments += `Project Name: ${v.project},\n`;
    comments += `Company: ${v.company},\n`;
    comments += `Phone: ${v.phone},\n`;

    if (v.email) {
      comments += `Email: ${v.email},\n`;
    }

    const checkboxes = v.checkboxes as { [key: string]: boolean };
    Object.keys(checkboxes).forEach(key => {
      if (checkboxes[key]) {
        comments += (newline === 0 ? key + ',' : '\n' + key + ',');
        newline++;
      }
    });

    if (v.notes) {
      comments += `\nCustomer Notes: ${v.notes}`;
    }

    request.comments = comments;
    request.details = `Date Inspection Needed: ${this.formatDate(v.inspdate)},\n`
      + `Project Name: ${v.project},\n`
      + (v.notes ? `Customer Notes: ${v.notes}` : '');

    return request;
  }

  private formatDate(date: Date): string {
    return date.toISOString().substring(0, 10);
  }
}
